import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Instagram, Twitter, Facebook, Youtube, Linkedin, MessageCircle,
    Save, RefreshCcw, ShieldCheck, Palette, Type, Link2, Star } from "lucide-react";

/**
 * Admin · Brand · Social Identity
 * Central registry for the six platforms + brand kit + footer preview.
 * Only Active profiles ever escape publicly — inactive ones remain
 * invisible on the marketing site and every generated creative.
 */

const ICONS = {
    Instagram: Instagram, Threads: MessageCircle, X: Twitter,
    LinkedIn: Linkedin, Facebook: Facebook, YouTube: Youtube,
};

export default function BrandSocialIdentity() {
    const [profiles, setProfiles] = useState([]);
    const [kit, setKit] = useState(null);
    const [footer, setFooter] = useState(null);
    const [dirty, setDirty] = useState(false);

    const load = async () => {
        const [p, k, f] = await Promise.all([
            api.get("/admin/brand/social-profiles").catch(() => null),
            api.get("/admin/brand/kit").catch(() => null),
            api.get("/public/brand/footer", { params: { platform: "Instagram" } }).catch(() => null),
        ]);
        setProfiles(p?.data?.profiles || []);
        setKit(k?.data || null);
        setFooter(f?.data?.footer || null);
        setDirty(false);
    };
    useEffect(() => { load(); }, []);
    // Re-fetch the footer preview whenever the marketing team toggles a
    // profile's Active / Show-in-footer flag so what's on screen reflects
    // the current state of the registry.
    useEffect(() => {
        api.get("/public/brand/footer", { params: { platform: "Instagram" } })
           .then(r => setFooter(r.data?.footer || null))
           .catch(() => {});
    }, [profiles]);

    const updateProfile = (platform, key, value) => {
        setProfiles(pp => pp.map(p => p.platform === platform ? { ...p, [key]: value } : p));
        setDirty(true);
    };

    const save = async () => {
        try {
            await api.patch("/admin/brand/social-profiles", { profiles });
            toast.success("Profiles saved");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    };

    const saveKit = async () => {
        try {
            await api.patch("/admin/brand/kit", kit);
            toast.success("Brand kit saved");
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    };

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="brand-social-identity">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <ShieldCheck size={12} /> Admin · Brand
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Social identity.</h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        One central registry. Only Active profiles appear publicly. No social handle is
                        hard-coded inside any template.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-stone-300 text-[13px] font-semibold text-stone-700 hover:border-stone-500"
                            data-testid="brand-refresh">
                        <RefreshCcw size={12} /> Refresh
                    </button>
                    <button onClick={save} disabled={!dirty}
                            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-semibold ${dirty ? "bg-stone-900 text-white hover:bg-stone-700" : "bg-stone-200 text-stone-500 cursor-not-allowed"}`}
                            data-testid="brand-save">
                        <Save size={12} /> Save profiles
                    </button>
                </div>
            </div>

            {/* Profiles table */}
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden mb-6"
                 data-testid="brand-profiles-table">
                <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-stone-50 border-b border-stone-100 text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">
                    <div className="col-span-2">Platform</div>
                    <div className="col-span-3">Handle · Display</div>
                    <div className="col-span-4">URL</div>
                    <div className="col-span-1 text-center">Active</div>
                    <div className="col-span-1 text-center">Verified</div>
                    <div className="col-span-1 text-center">Footer</div>
                </div>
                {profiles.map(p => {
                    const Icon = ICONS[p.platform] || Star;
                    return (
                        <div key={p.platform}
                             className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-stone-100 last:border-0 items-center"
                             data-testid={`brand-row-${p.platform}`}>
                            <div className="col-span-2 flex items-center gap-2 text-[13px] font-semibold text-stone-900">
                                <Icon size={14} className="text-stone-600" /> {p.platform}
                            </div>
                            <div className="col-span-3 space-y-1">
                                <input value={p.handle || ""} onChange={e => updateProfile(p.platform, "handle", e.target.value)}
                                       placeholder="@vaultbypd"
                                       className="w-full border border-stone-300 rounded px-2 py-1 text-[12.5px]"
                                       data-testid={`brand-handle-${p.platform}`} />
                                <input value={p.display_name || ""} onChange={e => updateProfile(p.platform, "display_name", e.target.value)}
                                       placeholder="VAULT by PD"
                                       className="w-full border border-stone-300 rounded px-2 py-1 text-[11.5px] text-stone-600"
                                       data-testid={`brand-display-${p.platform}`} />
                            </div>
                            <div className="col-span-4">
                                <input value={p.url || ""} onChange={e => updateProfile(p.platform, "url", e.target.value)}
                                       className="w-full border border-stone-300 rounded px-2 py-1 text-[12.5px]"
                                       data-testid={`brand-url-${p.platform}`} />
                            </div>
                            <div className="col-span-1 text-center">
                                <input type="checkbox" checked={!!p.active}
                                       onChange={e => updateProfile(p.platform, "active", e.target.checked)}
                                       data-testid={`brand-active-${p.platform}`} />
                            </div>
                            <div className="col-span-1 text-center">
                                <input type="checkbox" checked={!!p.verified}
                                       onChange={e => updateProfile(p.platform, "verified", e.target.checked)}
                                       data-testid={`brand-verified-${p.platform}`} />
                            </div>
                            <div className="col-span-1 text-center">
                                <input type="checkbox" checked={p.show_in_footer !== false}
                                       onChange={e => updateProfile(p.platform, "show_in_footer", e.target.checked)}
                                       data-testid={`brand-footer-${p.platform}`} />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Brand kit + footer preview */}
            {kit && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="brand-kit-card">
                        <div className="flex items-center gap-2 mb-3">
                            <Palette size={13} className="text-stone-600" />
                            <h2 className="text-lg font-display text-stone-900">Brand kit</h2>
                        </div>
                        <div className="space-y-3">
                            <KitInput label="Brand name"     value={kit.brand_name}     onChange={v => setKit({...kit, brand_name: v})} tid="kit-brand-name" />
                            <KitInput label="Primary handle" value={kit.primary_handle} onChange={v => setKit({...kit, primary_handle: v})} tid="kit-handle" />
                            <KitInput label="Website"        value={kit.website}        onChange={v => setKit({...kit, website: v})} tid="kit-website" />
                            <KitInput label="Logo URL"       value={kit.logo_url}       onChange={v => setKit({...kit, logo_url: v})} tid="kit-logo" />
                            <div className="grid grid-cols-3 gap-2 pt-2">
                                {Object.entries(kit.colours || {}).map(([k, v]) => (
                                    <div key={k} className="text-center">
                                        <div className="w-full h-8 rounded" style={{ background: v }} />
                                        <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mt-1">{k}</div>
                                        <div className="text-[10.5px] font-mono text-stone-700">{v}</div>
                                    </div>
                                ))}
                            </div>
                            <button onClick={saveKit}
                                    className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-700"
                                    data-testid="brand-kit-save">
                                <Save size={12} /> Save kit
                            </button>
                        </div>
                    </div>

                    <div className="bg-stone-900 rounded-2xl p-6 text-center overflow-hidden" data-testid="brand-footer-preview">
                        <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: kit.colours?.muted_gold || "#C9A96B" }}>
                            Footer preview · Instagram
                        </div>
                        <div className="my-8 text-[11px] font-mono" style={{ color: kit.colours?.warm_ivory || "#F6F3EC" }}>
                            (creative body)
                        </div>
                        <div className="border-t opacity-30 my-3" style={{ borderColor: kit.colours?.muted_gold || "#C9A96B" }} />
                        <div className="text-[12.5px]" style={{ color: kit.colours?.warm_ivory || "#F6F3EC" }}>
                            {footer?.compact || `${kit.primary_handle} | ${kit.website}`}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function KitInput({ label, value, onChange, tid }) {
    return (
        <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">{label}
            <input value={value || ""} onChange={e => onChange(e.target.value)}
                   className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                   data-testid={tid} />
        </label>
    );
}
